import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, MessageSquare, Send } from 'lucide-react';
import { getFullUrl } from '../lib/siteConfig';

interface SubscriptionSendReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: { subject?: string; message: string }) => Promise<void>;
  sendMethod: 'email' | 'sms';
  customerContact: string;
  customerFirstName: string;
  businessName: string;
  subscriptionName: string;
  amount: string;
  includesTax?: boolean;
  frequency: string;
  activationToken: string;
}

function formatFrequencyLabel(frequency: string): string {
  switch (frequency) {
    case 'daily': return 'daily';
    case 'weekly': return 'weekly';
    case 'monthly': return 'monthly';
    case 'every_3_months': return 'every 3 months';
    case 'every_6_months': return 'every 6 months';
    default: return frequency;
  }
}

export default function SubscriptionSendReviewModal({
  isOpen,
  onClose,
  onSend,
  sendMethod,
  customerContact,
  customerFirstName,
  businessName,
  subscriptionName,
  amount,
  includesTax = false,
  frequency,
  activationToken,
}: SubscriptionSendReviewModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const subscriptionUrl = getFullUrl(`/subscribe/${activationToken}`);
      const freqLabel = formatFrequencyLabel(frequency);
      const name = customerFirstName || 'there';
      const price = `$${parseFloat(amount).toFixed(2)}${includesTax ? ' including tax' : ''}`;

      if (sendMethod === 'email') {
        setSubject(`${subscriptionName} - Subscription from ${businessName}`);
        setMessage(
          `Hi ${name},\n\n` +
          `${businessName} has created a subscription for you:\n\n` +
          `${subscriptionName} - ${price} ${freqLabel}\n\n` +
          `Please use the link below to review and activate your subscription.\n\n` +
          `If you have any questions, please don't hesitate to contact us.`
        );
      } else {
        setMessage(
          `Hi ${name}, ${businessName} has set up a ${freqLabel} subscription for you: ${subscriptionName} (${price}). ` +
          `Review & activate here: ${subscriptionUrl}`
        );
      }
    }
  }, [isOpen, sendMethod, customerFirstName, businessName, subscriptionName, amount, includesTax, frequency, activationToken]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSubject('');
      setMessage('');
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    if (sendMethod === 'email' && !subject.trim()) return;

    try {
      setLoading(true);
      await onSend({ subject: sendMethod === 'email' ? subject : undefined, message });
      onClose();
    } catch (error) {
      console.error('Error sending:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isEmail = sendMethod === 'email';

  const modalContent = (
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      } z-[100070]`}
      style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`bg-white transform transition-all duration-200 fixed inset-0 w-full h-full sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[600px] sm:h-auto shadow-2xl ${
          isOpen ? 'translate-y-0 sm:translate-y-0 sm:translate-x-0 opacity-100' : 'translate-y-full sm:translate-y-0 sm:translate-x-full opacity-0 sm:opacity-100'
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
        onTransitionEnd={handleTransitionEnd}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{
              top: 'calc(1rem + env(safe-area-inset-top, 0px))',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center" style={{
            paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))',
          }}>
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              {isEmail ? 'Send Email' : 'Send SMS'}
            </h2>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto bg-white" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="p-4 sm:p-6 space-y-6">
              {/* Recipient */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">To</label>
                <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700">
                  {isEmail ? <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <MessageSquare className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                  {customerContact}
                </div>
              </div>

              {/* Subject (email only) */}
              {isEmail && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                    style={{ fontSize: '16px', minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                    placeholder="Enter email subject"
                  />
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  rows={isEmail ? 10 : 6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none touch-manipulation"
                  style={{ fontSize: '16px', WebkitTapHighlightColor: 'transparent' }}
                  placeholder="Enter your message"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    You can edit this message before sending.
                  </p>
                  {!isEmail && (
                    <p className="text-xs text-gray-400">{message.length} characters</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4" style={{
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          }}>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={loading || !message.trim() || (isEmail && !subject.trim())}
                className={`flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                  (!loading && message.trim() && (!isEmail || subject.trim()))
                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="h-4 w-4 mr-2" />
                    {isEmail ? 'Send Email' : 'Send SMS'}
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
